import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IPiggyBankModel } from "./IPiggyBankModel";
import { PiggyBankBenefitAtOpen } from "./PiggyBankBenefitAtOpen";
import { PiggyBankDto } from "../../../../networking/data/responses/piggybank/PiggyBankDto";
import { ObjectFieldsChecker } from "../../../../../tools/ObjectFieldsChecker";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";

@injectable()
export class PiggyBankModel extends Bindable implements IPiggyBankModel {

    private readonly networkClient: INetworkClient;
    private readonly connectionModel: IConnectionModel;
    private readonly logger: ILogger;

    public readonly step: NotifyProperty<number>;
    public readonly openAtLevel: NotifyProperty<number>;
    public readonly imageUrl: NotifyProperty<string>;
    public readonly name: NotifyProperty<string>;
    public readonly tokensBalance: NotifyProperty<string>;
    public readonly benefitsAtOpen: NotifyProperty<Array<PiggyBankBenefitAtOpen>>;

    public get piggyBankBenefistAtMinHours(): PiggyBankBenefitAtOpen {
        if (this.benefitsAtOpen.value.length === 0) {
            return null;
        }
        return this.benefitsAtOpen.value?.reduce((previous, current) => {
            return previous.buildingPurchaseCouponDurationInHours < current.buildingPurchaseCouponDurationInHours ? previous : current;
        });
    }

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.connectionModel = connectionModel;
        this.logger = logger;
        this.step = new NotifyProperty<number>(this, nameof(this, "step"), 0);
        this.openAtLevel = new NotifyProperty<number>(this, nameof(this, "openAtLevel"), 0);
        this.imageUrl = new NotifyProperty<string>(this, nameof(this, "tokensBalance"), "");
        this.name = new NotifyProperty<string>(this, nameof(this, "tokensBalance"), "");
        this.tokensBalance = new NotifyProperty<string>(this, nameof(this, "tokensBalance"), "0");
        this.benefitsAtOpen = new NotifyProperty<Array<PiggyBankBenefitAtOpen>>(this, nameof(this, "benefitsAtOpen"), null);
    }

    public async getPiggyBankStatus(): Promise<void> {
        try {
            if (!this.connectionModel.connected.value) {
                return;
            }
            const piggyBank = await this.networkClient.getPiggyBankStatus();
            this.validatePiggyBankDto(piggyBank);
            this.step.setValue(piggyBank.step);
            this.openAtLevel.setValue(piggyBank.openAtLevel);
            this.imageUrl.setValue(piggyBank.imageUrl);
            this.name.setValue(piggyBank.name);
            this.tokensBalance.setValue(piggyBank.tokensBalance);
            this.benefitsAtOpen.setValue(piggyBank.benefitsAtOpen?.map(item => new PiggyBankBenefitAtOpen(item)) ?? []);
        } catch (error) {
            this.logger.error(error);
        }
    }

    public updateStatusFromUserLevelUp(dto: PiggyBankDto): void {
        try {
            this.validatePiggyBankDto(dto);
            this.step.setValue(dto.step);
            this.openAtLevel.setValue(dto.openAtLevel);
            this.imageUrl.setValue(dto.imageUrl);
            this.name.setValue(dto.name);
            this.tokensBalance.setValue(dto.tokensBalance);
            this.benefitsAtOpen.setValue(dto.benefitsAtOpen?.map(item => new PiggyBankBenefitAtOpen(item)) ?? []);
        } catch (error) {
            this.logger.error(error);
        }
    }

    private validatePiggyBankDto(dto: PiggyBankDto): void {
        ObjectFieldsChecker.throwIfInvalidFields(dto, [
            "step",
            "openAtLevel",
            "tokensBalance",
            "imageUrl",
            "name"
        ]);
    }
}
